// Registro ÚNICO das telas. Cada tela é um chunk carregado sob demanda (`load`), e o menu,
// a busca global, a permissão e o prefetch saem daqui. Para adicionar uma tela: uma entrada
// aqui + a pasta `src/features/<slug>/` com `export default` do componente.
import type { ComponentType } from "react";
import type { Me, Permission } from "@/api/types";
import { can, passesRule, type AccessRule } from "@/access/permissions";
import type { IconName } from "@/ui/iconPaths";

export type GroupName = "Acompanhar" | "Operar" | "Configurar" | "Iuvo interno" | "Em breve";

export interface RouteDef {
  slug: string;
  label: string;
  group: GroupName;
  icon: IconName;
  permission?: Permission;
  rule?: AccessRule;
  subtitle?: string;
  /** Só Saúde da plataforma atualiza ao vivo; o resto é D-1. */
  live?: boolean;
  /** Contador demonstrativo no menu. */
  badge?: string;
  load: () => Promise<{ default: ComponentType }>;
}

const DEFAULT_SUBTITLE = "Acompanhe e gerencie sua operação em um só lugar.";

export const ROUTES: RouteDef[] = [
  { slug: "visao-geral", label: "Visão geral", group: "Acompanhar", icon: "dashboard", permission: "overview",
    subtitle: "Uma visão clara do que seu atendimento está entregando.", load: () => import("@/features/overview") },
  { slug: "conversas", label: "Conversas", group: "Acompanhar", icon: "message", permission: "conversations", badge: "74",
    subtitle: "Cada atendimento, do primeiro contato ao próximo passo.", load: () => import("@/features/conversations") },
  { slug: "qualidade", label: "Qualidade", group: "Acompanhar", icon: "shield", permission: "quality",
    subtitle: "Mais confiança em cada resposta do agente.", load: () => import("@/features/quality") },
  { slug: "pesquisas", label: "Pesquisas", group: "Acompanhar", icon: "report", permission: "surveys",
    subtitle: "Entenda a experiência por grupo de pacientes e os motivos por trás das respostas.", load: () => import("@/features/surveys") },
  { slug: "alertas", label: "Alertas", group: "Acompanhar", icon: "bell", permission: "alerts", badge: "3",
    load: () => import("@/features/alerts") },
  { slug: "relatorios", label: "Relatórios", group: "Acompanhar", icon: "report", permission: "reports",
    load: () => import("@/features/reports") },

  { slug: "templates", label: "Templates WhatsApp", group: "Operar", icon: "template", permission: "templates",
    subtitle: "Mensagens que mantêm o cuidado em movimento.", load: () => import("@/features/templates") },
  { slug: "base-de-conhecimento", label: "Base de conhecimento", group: "Operar", icon: "book", permission: "knowledge",
    subtitle: "Informações confiáveis para respostas melhores.", load: () => import("@/features/knowledge") },
  { slug: "auditoria-de-pareceres", label: "Auditoria de pareceres", group: "Operar", icon: "clipboard", permission: "medical", rule: "medical",
    subtitle: "Decisões acompanhadas, com critérios e rastreabilidade.", load: () => import("@/features/medical-audit") },

  { slug: "agentes", label: "Agentes", group: "Configurar", icon: "bot", permission: "agents",
    subtitle: "Conheça e acompanhe os agentes da sua operação.", load: () => import("@/features/agents") },
  { slug: "canais", label: "Canais", group: "Configurar", icon: "channels", permission: "channels",
    load: () => import("@/features/channels") },
  { slug: "solicitacoes", label: "Solicitações de mudança", group: "Configurar", icon: "changes", permission: "changes", badge: "4",
    subtitle: "Da sugestão à publicação, com segurança em cada etapa.", load: () => import("@/features/change-requests") },
  { slug: "usuarios", label: "Usuários e acessos", group: "Configurar", icon: "users", rule: "users",
    load: () => import("@/features/users") },
  { slug: "tokens", label: "Tokens e API", group: "Configurar", icon: "key", rule: "admin",
    load: () => import("@/features/tokens") },

  { slug: "organizacoes", label: "Organizações e unidades", group: "Iuvo interno", icon: "building", rule: "internal",
    load: () => import("@/features/orgs") },
  { slug: "custos", label: "Custos e consumo", group: "Iuvo interno", icon: "wallet", permission: "costs", rule: "admin",
    load: () => import("@/features/costs") },
  { slug: "versoes", label: "Versões e publicações", group: "Iuvo interno", icon: "layers", permission: "publish", rule: "admin",
    load: () => import("@/features/releases") },
  { slug: "saude-da-plataforma", label: "Saúde da plataforma", group: "Iuvo interno", icon: "pulse", rule: "internal", live: true,
    load: () => import("@/features/platform-health") },
  { slug: "log-de-auditoria", label: "Log de auditoria", group: "Iuvo interno", icon: "history", rule: "internal",
    load: () => import("@/features/audit-log") },

  { slug: "roadmap", label: "Roadmap", group: "Em breve", icon: "map", permission: "roadmap",
    load: () => import("@/features/roadmap") },
];

export const GROUPS: GroupName[] = ["Acompanhar", "Operar", "Configurar", "Iuvo interno", "Em breve"];

export const subtitleOf = (r: RouteDef) => r.subtitle ?? DEFAULT_SUBTITLE;

export function routeAllowed(r: RouteDef, me: Me): boolean {
  if (r.permission && !can(me, r.permission)) return false;
  return passesRule(me, r.rule);
}

export const allowedRoutes = (me: Me) => ROUTES.filter((r) => routeAllowed(r, me));
export const findRoute = (slug: string) => ROUTES.find((r) => r.slug === slug);
